/**
 * Modal con scrim + ESC + click fuera + portal.
 *
 * Sprint 14 S14-06c. Replica `.cb-modal-backdrop` + `.cb-modal` +
 * `.cb-modal__header` + `.cb-modal__title` + `.cb-modal__close` +
 * `.cb-modal__body` + `.cb-modal__footer` del handoff oficial
 * (`calbucura-design-handoff/design-system/components.css` líneas 861-916).
 *
 * El caller decide cuándo montar (`{open && <Modal />}`) — no hay prop
 * `open` interno. Sigue el patrón del mock `mis-datos.html` donde el
 * modal se renderiza condicionalmente.
 *
 * Comportamiento:
 * - ESC cierra (listener global mientras montado).
 * - Click en el backdrop cierra; click en el modal no propaga.
 * - Mount via createPortal a document.body (z-index del handoff: 900/1000).
 * - role="dialog" + aria-modal="true" + aria-labelledby al título.
 *
 * Uso:
 *   {editing && (
 *     <Modal
 *       title="Agregar residente"
 *       onClose={() => setEditing(false)}
 *       footer={
 *         <>
 *           <Button variant="ghost" onClick={() => setEditing(false)}>Cancelar</Button>
 *           <Button variant="primary" onClick={save}>Guardar</Button>
 *         </>
 *       }
 *     >
 *       <FormContent />
 *     </Modal>
 *   )}
 */
import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  /** Título mostrado en el header. Usado también como aria-labelledby. */
  title: ReactNode;
  /** Llamado cuando el usuario presiona ESC, clickea el backdrop, o el botón X. */
  onClose: () => void;
  /** Contenido del modal body. */
  children: ReactNode;
  /** Slot de footer (botones de acción). Opcional. */
  footer?: ReactNode;
  className?: string;
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  className = '',
}: ModalProps) {
  const titleId = useId();

  // ESC cierra. Listener global mientras el modal esté montado.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const modal = (
    <div
      className="fixed inset-0 bg-black/60 z-modal-backdrop flex items-center justify-center p-4"
      onMouseDown={(e) => {
        // Solo cierra si el click es en el backdrop, no en el modal.
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="modal-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`bg-bg-elevated border-[0.5px] border-line-strong rounded-lg shadow-lg max-w-[480px] w-full max-h-[90vh] overflow-y-auto z-modal ${className}`.trim()}
      >
        <div className="py-5 px-6 border-b-[0.5px] border-line flex justify-between items-center">
          <h3 id={titleId} className="text-xl font-medium text-text-primary m-0">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="bg-transparent border-0 text-text-secondary cursor-pointer p-1 hover:text-text-primary transition-colors duration-base"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="p-6">{children}</div>

        {footer && (
          <div className="py-4 px-6 border-t-[0.5px] border-line flex justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // SSR-safe: si no hay document (entorno node sin jsdom), no portea.
  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
